import { useEffect, useRef } from 'react';
import api from '../utils/api';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
// Shared across all tabs via localStorage — any tab's activity resets the global clock
const LAST_ACTIVITY_KEY = 'last_activity_at';

/**
 * Monitors user inactivity. After `timeoutMs` of no browser activity (across ALL tabs):
 * 1. Shows the session-expired overlay (preserving all app state).
 * 2. Fires a signal to the backend to revoke the refresh token (fire-and-forget).
 *
 * Multi-tab safe: if another tab was recently active, the timer resets instead of firing.
 * This prevents Tab A (idle) from revoking the session while Tab B is still in use.
 *
 * @param {Function} onInactive  - callback to show overlay (e.g. () => setShowSessionExpired(true))
 * @param {boolean}  isActive    - whether to run the monitor (true when authenticated)
 * @param {number}   timeoutMs   - inactivity threshold in ms (default: 30 minutes)
 */
export function useInactivityMonitor(onInactive, isActive, timeoutMs = 30 * 60 * 1000) {
  const timerRef = useRef(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!isActive) return;

    firedRef.current = false;

    const handleInactive = () => {
      if (firedRef.current) return;

      // Check if another tab was recently active (shared via localStorage)
      const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || '0', 10);
      if (Date.now() - lastActivity < timeoutMs) {
        // Another tab is still active — defer this tab's timer to match
        resetTimer(timeoutMs - (Date.now() - lastActivity));
        return;
      }

      firedRef.current = true;

      // Show overlay immediately — don't await the signal
      onInactive();

      // Tell backend to revoke the token (fire-and-forget)
      api.signalInactive().catch(() => {});
    };

    const resetTimer = (delay = timeoutMs) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleInactive, delay);
    };

    const onActivity = () => {
      // Stamp the global activity clock so other tabs can see this tab is alive
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      resetTimer();
    };

    // Initialize cross-tab clock and start timer
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    resetTimer();
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));

    return () => {
      clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
    };
  }, [isActive, onInactive, timeoutMs]);
}
